import { removeLocalStorage } from '@omnia/ui/src/lib/utils/localStorage'
import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-v8'
import { scrollToTop } from '../components/scrollToTop/scrollToTop'
import { getLocalStorage } from '../utils/localStorage/getLocalStorage'

const SCROLL_RESTORATION_TIMEOUT_MS = 3000
const SCROLL_ANIMATION_DURATION_MS = 1000

const scrollPositions = new Map<string, number>()
const scrollPositionsByPath = new Map<string, number>()

const smoothScrollTo = (targetY: number, isCancelled: () => boolean): void => {
    const startY = window.scrollY
    const distance = targetY - startY
    const startTime = performance.now()

    const easeInOut = (progress: number): number => {
        return progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2
    }

    const animate = (currentTime: number): void => {
        if (isCancelled()) return

        const progress = Math.min((currentTime - startTime) / SCROLL_ANIMATION_DURATION_MS, 1)

        const easedProgress = easeInOut(progress)

        window.scrollTo(0, startY + distance * easedProgress)

        if (progress < 1) {
            requestAnimationFrame(animate)
        }
    }

    requestAnimationFrame(animate)
}

export const useScrollRestoration = (): void => {
    const location = useLocation()
    const navigationType = useNavigationType()

    useEffect(() => {
        if (!('scrollRestoration' in window.history)) return

        window.history.scrollRestoration = 'manual'

        return () => {
            window.history.scrollRestoration = 'auto'
        }
    }, [])

    useEffect(() => {
        const savePosition = (): void => {
            scrollPositions.set(location.key, window.scrollY)
            scrollPositionsByPath.set(location.pathname, window.scrollY)
        }

        window.addEventListener('scroll', savePosition, {
            passive: true,
        })

        return () => {
            window.removeEventListener('scroll', savePosition)
        }
    }, [location.key, location.pathname])

    useEffect(() => {
        // specific situation, where we do not go back in history, but we
        // navigate to a specific page.
        // we need to take care of each of those specific scenarios, for the back button
        // when we need to restore scroll position
        const isReturningFromBookingFlow = getLocalStorage<boolean>('restoreScrollOnBookingFlowReturn') === true

        if (isReturningFromBookingFlow) {
            removeLocalStorage('restoreScrollOnBookingFlowReturn')
        }

        const isPop = String(navigationType) === 'POP'

        const shouldRestoreScroll = isPop || isReturningFromBookingFlow

        if (!shouldRestoreScroll) {
            const timeoutId = scrollToTop()

            return () => {
                clearTimeout(timeoutId)
            }
        }

        const savedY = scrollPositions.get(location.key) ?? scrollPositionsByPath.get(location.pathname)

        if (savedY == null) return

        let cancelled = false
        const startTime = performance.now()

        const restore = (): void => {
            if (cancelled) return

            const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)

            if (maxScroll >= savedY) {
                smoothScrollTo(savedY, () => cancelled)
                return
            }

            if (performance.now() - startTime >= SCROLL_RESTORATION_TIMEOUT_MS) {
                smoothScrollTo(Math.min(savedY, maxScroll), () => cancelled)
                return
            }

            requestAnimationFrame(restore)
        }

        requestAnimationFrame(restore)

        return () => {
            cancelled = true
        }
    }, [location.key, location.pathname, navigationType])
}
